import type { LogContext, LogLevel } from './types';
import type { LoggingConfig } from './config';
import { RemoteLogTransport } from './remoteTransport';

const LEVEL_PRIORITY: Record<LogLevel, number> = {
  trace: 0,
  debug: 1,
  info: 2,
  warn: 3,
  error: 4,
  silent: 5,
};

export class Logger {
  private config: LoggingConfig;
  private remoteTransport: RemoteLogTransport | null;

  constructor(config: LoggingConfig) {
    this.config = config;
    this.remoteTransport = config.remoteLoggingEnabled
      ? new RemoteLogTransport(config)
      : null;
  }

  private shouldLog(level: LogLevel): boolean {
    return LEVEL_PRIORITY[level] >= LEVEL_PRIORITY[this.config.logLevel];
  }

  private formatMessage(level: string, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const levelStr = level.toUpperCase().padEnd(5);
    const contextStr = context && Object.keys(context).length > 0
      ? ` | ${JSON.stringify(context)}`
      : '';
    return `[${timestamp}] ${levelStr} ${message}${contextStr}`;
  }

  private log(
    level: LogLevel,
    consoleMethod: (...args: unknown[]) => void,
    message: string,
    context?: LogContext
  ): void {
    if (!this.shouldLog(level)) return;
    consoleMethod(this.formatMessage(level, message, context));
    this.remoteTransport?.send(level, message, context);
  }

  trace(message: string, context?: LogContext): void {
    this.log('trace', console.debug, message, context);
  }

  debug(message: string, context?: LogContext): void {
    this.log('debug', console.debug, message, context);
  }

  info(message: string, context?: LogContext): void {
    this.log('info', console.log, message, context);
  }

  warn(message: string, context?: LogContext): void {
    this.log('warn', console.warn, message, context);
  }

  error(message: string, error?: Error, context?: LogContext): void {
    const errorContext: LogContext = {
      ...context,
      error: error
        ? {
            name: error.name,
            message: error.message,
            stack: error.stack,
          }
        : undefined,
    };
    this.log('error', console.error, message, errorContext);
  }

  setLevel(level: LogLevel): void {
    this.config = { ...this.config, logLevel: level };
  }

  getLevel(): LogLevel {
    return this.config.logLevel;
  }
}
